import {
  DocumentOrganizationKeys,
  FilterDocument,
  FilterDocumentFilters,
  FilterOptionKeys,
  GoalFilterDocument,
} from '@griply/types';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import {
  GoalSortingMode,
  GroupByMode,
  SortingMode,
} from 'shared/types/sorting-mode';

export const defaultFilter = (
  goal: Goal | undefined,
  subGoalIds: ID[],
): FilterDocument => {
  const include = [goal?.id ?? '', ...subGoalIds];
  return {
    sortingMode: SortingMode.Custom,
    groupByMode: GroupByMode.Section,
    filters: {
      [FilterOptionKeys.Goal]: {
        include,
        availableOptions: include,
      },
    },
  };
};

export const getFilterDocument = (
  goal: Goal | undefined,
  subGoalIds: ID[],
): FilterDocument => {
  const base = defaultFilter(goal, subGoalIds);

  const storedGoalFilter =
    goal?.filterDocument?.filters?.[FilterOptionKeys.Goal];
  const freshInclude = base.filters?.[FilterOptionKeys.Goal]?.include ?? [];

  // Reconcile goal filter: preserve user toggle choices,
  // but add newly added sub-goals and remove stale ones.
  // Uses `available` to track which IDs existed last time the filter was saved,
  // so we can distinguish "user toggled off" from "brand new sub-goal".
  let reconciledGoalFilter: FilterDocumentFilters[FilterOptionKeys.Goal] = {
    ...base.filters?.[FilterOptionKeys.Goal],
    availableOptions: freshInclude,
  };

  if (storedGoalFilter?.include) {
    const storedInclude = storedGoalFilter.include;
    const storedAvailable = storedGoalFilter.availableOptions ?? storedInclude;
    // Keep stored IDs that are still valid (removes deleted sub-goals)
    const kept = storedInclude.filter((id: ID) => freshInclude.includes(id));
    // Only add IDs that are truly new (not in previous available set)
    const added = freshInclude.filter(
      (id: ID) => !storedAvailable.includes(id),
    );
    reconciledGoalFilter = {
      ...storedGoalFilter,
      include: [...kept, ...added],
      availableOptions: freshInclude,
    };
  }

  return {
    ...base,
    ...(goal?.filterDocument ?? {}),
    showCompleted: !(goal?.hideCompletedTasks ?? true),
    [DocumentOrganizationKeys.Filters]: {
      ...base.filters,
      ...(goal?.filterDocument?.filters ?? {}),
      [FilterOptionKeys.Goal]: reconciledGoalFilter,
    },
  };
};

export const getGoalFilterDocument = (
  goal: Goal | undefined,
  subGoalIds: ID[],
): GoalFilterDocument => {
  const base = goal?.goalFilterDocument ?? {
    [DocumentOrganizationKeys.GroupByMode]: GroupByMode.None,
  };

  return {
    ...base,
    [DocumentOrganizationKeys.GroupByMode]:
      base.groupByMode ?? GroupByMode.None,
    [DocumentOrganizationKeys.SortingMode]:
      base.sortingMode ?? goal?.goalSortingMode ?? GoalSortingMode.StartDate,
    [DocumentOrganizationKeys.Filters]: {
      ...(base[DocumentOrganizationKeys.Filters] ?? {}),
      [FilterOptionKeys.Goal]: {
        include: [...subGoalIds],
      },
    },
    [DocumentOrganizationKeys.CollapsedSections]:
      base[DocumentOrganizationKeys.CollapsedSections] ??
      goal?.collapsedSubGoals ??
      subGoalIds,
  };
};
